import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button, Input } from "../components/ui";
import { BrandLogo } from "../components/BrandLogo";

type LoginStep = "email" | "password" | "mfa";

interface LoginPrefetchProfile {
  email: string;
  displayName: string;
  avatarUrl: string | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
  return value.toLowerCase().trim();
}

function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(normalizeEmail(value));
}

async function fetchLoginPrefetch(email: string): Promise<LoginPrefetchProfile | null> {
  const data = await api<{ profile: LoginPrefetchProfile | null }>("/auth/login/prefetch", {
    body: { email },
  });
  return data.profile;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const login = useAuth((s) => s.login);
  const verifyMfa = useAuth((s) => s.verifyMfa);

  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [mfaMethods, setMfaMethods] = useState<("email" | "totp")[]>([]);
  const [mfaMethod, setMfaMethod] = useState<"email" | "totp">("email");
  const [mfaCode, setMfaCode] = useState("");

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const normalizedEmail = normalizeEmail(email);

  const prefetchQuery = useQuery({
    queryKey: ["login-prefetch", normalizedEmail],
    queryFn: () => fetchLoginPrefetch(normalizedEmail),
    enabled: step === "password" && isValidEmail(email),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (step === "password") {
      passwordRef.current?.focus();
    } else if (step === "email") {
      emailRef.current?.focus();
    }
  }, [step]);

  function goToPasswordStep() {
    if (!isValidEmail(email)) {
      toast("error", "Enter a valid email address");
      emailRef.current?.focus();
      return;
    }
    const trimmed = normalizeEmail(email);
    setEmail(trimmed);
    setStep("password");
    void queryClient.prefetchQuery({
      queryKey: ["login-prefetch", trimmed],
      queryFn: () => fetchLoginPrefetch(trimmed),
      staleTime: 5 * 60_000,
    });
  }

  function goToEmailStep() {
    setStep("email");
    setPassword("");
    setMfaCode("");
    setChallengeToken("");
    setMfaMethods([]);
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim()) {
      toast("error", "Enter your password");
      passwordRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const result = await login(normalizedEmail, password);
      if (!result.ok) {
        setChallengeToken(result.mfaRequired.challengeToken);
        setMfaMethods(result.mfaRequired.methods);
        setMfaMethod(result.mfaRequired.methods[0] ?? "email");
        setStep("mfa");
        if (result.mfaRequired.methods.includes("email")) {
          toast("info", "Verification code sent to your email");
        }
        return;
      }
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? "/", { replace: true });
    } catch (err) {
      let message = "Login failed";
      if (err instanceof ApiError) message = err.message;
      else if (err instanceof Error) message = err.message;
      toast("error", message);
      passwordRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaCode.trim()) {
      toast("error", "Enter your verification code");
      return;
    }
    setBusy(true);
    try {
      await verifyMfa(challengeToken, mfaCode.trim(), mfaMethod);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? "/", { replace: true });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Invalid verification code");
    } finally {
      setBusy(false);
    }
  }

  const profile = prefetchQuery.data;
  const prefetchLoading = step === "password" && prefetchQuery.isFetching && !prefetchQuery.data;

  return (
    <div className="flex min-h-full flex-col justify-center bg-brand-dark px-6 py-12">
      <div className="mx-auto w-full max-w-md">
        <div className="rounded-2xl border border-line bg-surface p-6 shadow-xl sm:p-8">
          <BrandLogo className="h-9 w-auto max-w-[200px] object-contain" />

          {step === "email" ? (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold text-white">Sign in</h1>
              <p className="mt-1 text-sm text-white/50">Use your staff account email to continue</p>
              <form
                className="mt-6 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  goToPasswordStep();
                }}
              >
                <Input
                  ref={emailRef}
                  type="email"
                  label="Email"
                  placeholder="name@company.com"
                  hint="e.g. admin@mrgaole.com"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex justify-end pt-1">
                  <Button type="submit">Next</Button>
                </div>
              </form>
            </>
          ) : step === "password" ? (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold text-white">Enter your password</h1>
              <p className="mt-1 text-sm text-white/50">Sign in to continue to Backoffice</p>

              <div className="mt-5 rounded-xl border border-line bg-white/[0.03] px-3 py-2.5">
                <div className="flex items-center gap-3">
                  {profile?.avatarUrl ? (
                    <img src={profile.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-sm font-bold text-white">
                      {(profile?.displayName ?? normalizedEmail).slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Email</p>
                    <p className="truncate text-sm font-medium text-white">{normalizedEmail}</p>
                    {profile?.displayName && (
                      <p className="truncate text-xs text-white/50">{profile.displayName}</p>
                    )}
                    {prefetchLoading && !profile && (
                      <p className="text-xs text-white/40">Loading account…</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={goToEmailStep}
                    className="shrink-0 text-xs font-semibold text-brand-pink hover:underline"
                  >
                    Change
                  </button>
                </div>
              </div>

              <form className="mt-5 space-y-4" onSubmit={submitPassword}>
                <Input
                  ref={passwordRef}
                  type="password"
                  label="Password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={goToEmailStep}
                    className="text-sm font-medium text-brand-pink hover:underline"
                  >
                    Back
                  </button>
                  <Button type="submit" disabled={busy}>
                    {busy ? "Signing in…" : "Sign in"}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold text-white">Verify your identity</h1>
              <p className="mt-1 text-sm text-white/50">
                {mfaMethod === "email"
                  ? "Enter the code sent to your email"
                  : "Enter the code from your authenticator app"}
              </p>

              {mfaMethods.length > 1 && (
                <div className="mt-4 flex gap-2">
                  {mfaMethods.includes("email") && (
                    <button
                      type="button"
                      onClick={() => setMfaMethod("email")}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        mfaMethod === "email" ? "bg-gradient-brand text-white" : "bg-white/10 text-white/60"
                      }`}
                    >
                      Email
                    </button>
                  )}
                  {mfaMethods.includes("totp") && (
                    <button
                      type="button"
                      onClick={() => setMfaMethod("totp")}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        mfaMethod === "totp" ? "bg-gradient-brand text-white" : "bg-white/10 text-white/60"
                      }`}
                    >
                      Authenticator
                    </button>
                  )}
                </div>
              )}

              <form className="mt-5 space-y-4" onSubmit={submitMfa}>
                <Input
                  label="Verification code"
                  placeholder="Enter code"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("password");
                      setMfaCode("");
                    }}
                    className="text-sm font-medium text-brand-pink hover:underline"
                  >
                    Back
                  </button>
                  <Button type="submit" disabled={busy}>
                    {busy ? "Verifying…" : "Verify"}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
